import { type AuthUserDto, authSignOut, COMMON_CACHE_KEYS, type RefreshTokenResponse, type SignInResult } from "@serophin/shared";
import { cache } from "./cache";

export const AUTH_CACHE_KEYS = COMMON_CACHE_KEYS;

/**
 * Keeps a sign-in or refresh answer. An empty `refreshToken` means the server put it in its HttpOnly cookie: only a marker
 * with its expiry is kept, so the page knows a session exists without ever holding the token. A server without the refresh
 * cookie answers with the token, which is kept as before.
 */
export const setAuthTokenPair = (response: RefreshTokenResponse): void => {
  cache.set(AUTH_CACHE_KEYS.accessToken, response.accessToken, response.accessTokenExpiresAt);
  if (response.refreshToken) {
    cache.set(AUTH_CACHE_KEYS.refreshToken, response.refreshToken, response.refreshTokenExpiresAt);
    cache.remove(AUTH_CACHE_KEYS.refreshCookie);
    return;
  }
  cache.set(AUTH_CACHE_KEYS.refreshCookie, true, response.refreshTokenExpiresAt);
  cache.remove(AUTH_CACHE_KEYS.refreshToken);
};

export const setAuthTokens = (response: SignInResult): void => {
  setAuthTokenPair(response);
  cache.set(AUTH_CACHE_KEYS.user, response.user);
};

export const setAuthUser = (user: AuthUserDto): void => {
  cache.set(AUTH_CACHE_KEYS.user, user);
};

export const getAuthUser = (): AuthUserDto | null => {
  return cache.get<AuthUserDto>(AUTH_CACHE_KEYS.user);
};

export const getAccessToken = (): string | null => {
  return cache.get<string>(AUTH_CACHE_KEYS.accessToken);
};

/** The refresh token, only where the server still answers it in the body; null when it lives in the HttpOnly cookie. */
export const getRefreshToken = (): string | null => {
  return cache.get<string>(AUTH_CACHE_KEYS.refreshToken);
};

/** True while the session can be renewed: a refresh token in storage or (unexpired) in the HttpOnly cookie. */
export const hasRefreshSession = (): boolean => getRefreshToken() !== null || cache.get<boolean>(AUTH_CACHE_KEYS.refreshCookie) === true;

/** True while the browser holds a usable access token or can renew one. */
export const hasSession = (): boolean => getAccessToken() !== null || hasRefreshSession();

export const clearAuth = (): void => {
  cache.remove(AUTH_CACHE_KEYS.accessToken);
  cache.remove(AUTH_CACHE_KEYS.refreshToken);
  cache.remove(AUTH_CACHE_KEYS.refreshCookie);
  cache.remove(AUTH_CACHE_KEYS.user);
};

/**
 * Drops the tokens here at once, then revokes the session on the server (its refresh token, from storage or the cookie, which
 * the answer also clears). Best effort: a server that is unreachable or already forgot the session still leaves this browser
 * signed out.
 */
export const endSession = async (): Promise<void> => {
  const refreshToken = getRefreshToken() ?? undefined;
  clearAuth();
  try {
    await authSignOut.invoke(refreshToken === undefined ? {} : { refreshToken }, { skipAuth: true });
  } catch (error) {
    console.error("Failed to sign out on the server:", error);
  }
};

/** Ends the session (see endSession), clears everything cached and goes to the home page. */
export const signOut = (): void => {
  void endSession().finally(() => {
    cache.removeByPrefix("cache:");
    window.location.href = "/";
  });
};
